import type { LanguageVoice } from "@repo/app-types/constants/languages";
import {
  allInterviewLanguages,
  getDefaultVoiceForLanguage,
  getVoicesForLanguage,
  InterviewLanguage,
  LANGUAGE_DISPLAY_NAMES,
  LANGUAGE_TO_FLAG,
  languageVoiceDisplayNames,
  multilangLanguages,
} from "@repo/app-types/constants/languages";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/index";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import isEqual from "lodash-es/isEqual.js";
import { useState } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

const languageOptions = allInterviewLanguages.map((value) => ({
  label: `${LANGUAGE_TO_FLAG[value]}\u00A0\u00A0${LANGUAGE_DISPLAY_NAMES[value]}`,
  value,
}));

interface MultilangLanguageOption {
  id: InterviewLanguage;
  name: string;
}

const multilangLanguageOptions: MultilangLanguageOption[] =
  multilangLanguages.map((language) => ({
    id: language,
    name: `${LANGUAGE_TO_FLAG[language]}\u00A0\u00A0${LANGUAGE_DISPLAY_NAMES[language]}`,
  }));

interface InterviewLanguageSettingProps {
  language: InterviewLanguage;
  multilangLanguages: InterviewLanguage[] | null;
  voice: LanguageVoice | null;
  isTrainingProject: boolean;
  onSave: (
    language: InterviewLanguage,
    multilangLanguages: InterviewLanguage[],
    voice: LanguageVoice | null,
  ) => void;
}

export const InterviewLanguageSetting = ({
  language,
  multilangLanguages,
  voice,
  isTrainingProject,
  onSave,
}: InterviewLanguageSettingProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [selectedMultilangLanguages, setSelectedMultilangLanguages] = useState<
    InterviewLanguage[]
  >(multilangLanguages || []);
  const [selectedVoice, setSelectedVoice] = useState<LanguageVoice | null>(
    voice,
  );

  const availableVoices = getVoicesForLanguage(selectedLanguage);
  const voiceOptions = availableVoices.map((voice) => ({
    label: languageVoiceDisplayNames[voice],
    value: voice,
  }));

  const hasLanguageChanged = selectedLanguage !== language;

  const hasMultilangLanguagesChanged =
    selectedLanguage === InterviewLanguage.MULTILANG &&
    !isEqual(
      (multilangLanguages || []).toSorted(),
      selectedMultilangLanguages.toSorted(),
    );

  // When the current voice is null, we display the default voice for the language.
  // We don't want to consider displaying the default voice in that case as a voice change.
  const hasVoiceChanged =
    selectedVoice !== voice &&
    (voice || selectedVoice !== getDefaultVoiceForLanguage(selectedLanguage));

  const hasChanges =
    hasLanguageChanged || hasMultilangLanguagesChanged || hasVoiceChanged;

  const voiceToDisplay = selectedVoice || getDefaultVoiceForLanguage(language);

  // All languages are available
  const availableLanguageOptions = languageOptions;
  const languageLabel = isTrainingProject
    ? "Training language"
    : "Interview language";
  const copyDescription = isTrainingProject
    ? "Select the language that the AI trainer will use during training sessions. Your training materials do not need to match this language."
    : "Select the language that the AI interviewer will use during interviews. Your interview questions do not need to match this language.";

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <div className="space-y-2">
            <div className="flex flex-row gap-4">
              <div className="grid min-w-[280px]">
                <Label size={Size.SMALL} className="mb-2 block">
                  {languageLabel}
                </Label>
                <SimpleSelect
                  value={selectedLanguage}
                  onChange={(changedLanguage) => {
                    setSelectedLanguage(changedLanguage);
                    // If we select back the original language, also set back the original voice
                    if (changedLanguage === language) {
                      setSelectedMultilangLanguages(multilangLanguages || []);
                      setSelectedVoice(voice);
                    } else {
                      setSelectedMultilangLanguages([]);
                      setSelectedVoice(
                        getDefaultVoiceForLanguage(changedLanguage),
                      );
                    }
                  }}
                  options={availableLanguageOptions}
                  isDisabled={!allowed}
                />
              </div>

              {availableVoices.length > 0 ? (
                <div className="grid min-w-[280px]">
                  <Label size={Size.SMALL} className="mb-2 block">
                    Voice
                  </Label>
                  <SimpleSelect
                    value={voiceToDisplay}
                    onChange={setSelectedVoice}
                    options={voiceOptions}
                    isDisabled={!allowed}
                  />
                </div>
              ) : null}

              {selectedLanguage === InterviewLanguage.MULTILANG ? (
                <div className="grid min-w-[280px]">
                  <ComboboxField<MultilangLanguageOption, "multiple">
                    label="Multilingual languages"
                    selectionMode="multiple"
                    items={multilangLanguageOptions}
                    getLabel={(option) => option.name}
                    value={selectedMultilangLanguages}
                    onChange={(keys) => {
                      setSelectedMultilangLanguages(
                        keys.map((key) => String(key) as InterviewLanguage),
                      );
                    }}
                    placeholder="Add a language"
                    chipsLabel="Selected languages"
                    isDisabled={!allowed}
                  />
                </div>
              ) : null}
            </div>

            <div className="text-sm text-muted-foreground">
              {copyDescription}{" "}
              <Link href="mailto:support@alpharun.com">
                Reach out to support
              </Link>{" "}
              to access additional languages.
              {selectedLanguage === InterviewLanguage.MULTILANG &&
                selectedMultilangLanguages.length < 2 && (
                  <div>
                    Multilingual requires you to select at least 2 languages.
                  </div>
                )}
            </div>
            {hasChanges ? (
              <SaveAndCancelButtons
                isSaveDisabled={
                  selectedLanguage === InterviewLanguage.MULTILANG &&
                  selectedMultilangLanguages.length < 2
                }
                onSave={() => {
                  onSave(
                    selectedLanguage,
                    selectedMultilangLanguages,
                    selectedVoice,
                  );
                }}
                onCancel={() => {
                  setSelectedLanguage(language);
                  setSelectedVoice(voice);
                  setSelectedMultilangLanguages(multilangLanguages || []);
                }}
              />
            ) : null}
          </div>
        </>
      )}
    </Can>
  );
};
